import React, { useEffect, useState } from 'react';

import { Spinner } from 'components/Spinner/spinner';
import ResizableColumns from 'components/ResizableColumns/ResizableColumns';
import ResizableRows from 'components/ResizableRows/ResizableRows';
import FreecodeTabSelector from 'components/FreecodeTabSelector/FreecodeTabSelector';
import { useFreecodeContext } from 'hooks/useContext';
import { identifiers, tabLabels } from './FreecodeLayoutConstants';
import { LanguageSelection } from 'components/FreecodeContent/FreecodeButtons';

const descriptionContainerOptions = [identifiers.description, identifiers.summary, identifiers.course, identifiers.api];

const solutionContainerOptions = [
  identifiers.implementation,
  identifiers.solution,
  identifiers.discussion,
  identifiers.apiSettings,
  identifiers.graphicsSettings,
  identifiers.settings,
  identifiers.preview,
];

/**
 * Given a map with component display them in two columns
 */
export default function FreecodeLayoutDefault({ componentMap }) {
  const [leftColumnItems, setLeftColumnItems] = useState([]);
  const [rightColumnItems, setRightColumnItems] = useState([]);
  const [canvasPercentage, setCanvasPercentage] = useState(25);

  const { testImpl } = useFreecodeContext();

  useEffect(() => {
    let newLeftColumn = [];
    let newRightColumn = [];

    let TabOptionsDescription = [];
    for (const keyName of descriptionContainerOptions) {
      if (componentMap[keyName]) TabOptionsDescription.push(keyName);
    }

    if (TabOptionsDescription.length) newLeftColumn.push(TabOptionsDescription);

    if (componentMap[identifiers.canvas]) newLeftColumn.push(identifiers.canvas);

    let TabOptionsSolution = [];
    for (const keyName of solutionContainerOptions) {
      if (componentMap[keyName]) TabOptionsSolution.push(keyName);
    }

    if (TabOptionsSolution.length) newRightColumn.push(TabOptionsSolution);

    if (componentMap[identifiers.console]) newRightColumn.push(identifiers.console);

    setLeftColumnItems(newLeftColumn);
    setRightColumnItems(newRightColumn);
  }, [componentMap]);

  useEffect(() => {
    if (!leftColumnItems.includes(identifiers.canvas)) return;
    // Offset header height
    let columnHeight = window.innerHeight - 55;
    let columnWidth = window.innerWidth * 0.4;

    let widthUnit = columnWidth / testImpl.configCanvas.width;
    // offset canvas header, footer, and draggable gutter size
    let desiredCanvasHeight = widthUnit * testImpl.configCanvas.height + 78;

    let desiredHeightPercentage = (100 * desiredCanvasHeight) / columnHeight;

    setCanvasPercentage(Math.min(desiredHeightPercentage, 75));
    window.dispatchEvent(new Event('resize'));
  }, [testImpl.configCanvas, leftColumnItems]);

  const renderDescriptionTabSelector = () => {
    return (
      leftColumnItems?.[0]?.length && (
        <FreecodeTabSelector
          key="DescriptionTabSelector"
          items={leftColumnItems[0].map((KeyName, index) => ({
            label: tabLabels[KeyName]?.label,
            icon: tabLabels[KeyName]?.icon,
            tabId: tabLabels[KeyName]?.tabId,
            component: componentMap[KeyName],
          }))}
        />
      )
    );
  };

  const renderLeftColumn = () => {
    if (!leftColumnItems.length) return <Spinner key="leftSpinner" />;
    if (leftColumnItems.length === 1) return renderDescriptionTabSelector();
    return (
      <ResizableRows key="LeftColumn" sizes={[100 - canvasPercentage, canvasPercentage]}>
        {renderDescriptionTabSelector()}
        {componentMap[leftColumnItems[1]]}
      </ResizableRows>
    );
  };

  const renderSolutionTabSelector = () => {
    return (
      rightColumnItems?.[0]?.length && (
        <FreecodeTabSelector
          key="SolutionTabSelector"
          items={rightColumnItems[0].map((KeyName, index) => ({
            label: tabLabels[KeyName]?.label,
            icon: tabLabels[KeyName]?.icon,
            tabId: tabLabels[KeyName]?.tabId,
            component: componentMap[KeyName],
          }))}
          headerComponents={<LanguageSelection />}
        />
      )
    );
  };

  const renderRightColumn = () => {
    if (!rightColumnItems.length) return <Spinner key="rightSpinner" />;
    if (rightColumnItems.length === 1) return renderSolutionTabSelector();
    return (
      <ResizableRows key="RightColumn" sizes={[75, 25]}>
        {renderSolutionTabSelector()}
        {componentMap[rightColumnItems[1]]}
      </ResizableRows>
    );
  };

  return (
    <ResizableColumns key="defaultLayout" sizes={[40, 60]}>
      {renderLeftColumn()}
      {renderRightColumn()}
    </ResizableColumns>
  );
}
